import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import AdminNavbar from '../../components/AdminNavbar';
import AdminBreadcrumb from '../../components/AdminBreadcrumb';
import { 
    fetchSingleCatalog, fetchAdminProducts, saveProduct, 
    clearAdminError, setProductCover, deleteProductImage 
} from '../../store/slices/adminSlice';
import { getCurrencySymbol } from '../../helper/currencyHelper';
import { resolveImageUrl } from '../../helper/imageHelper';

const STOCK_STATUSES = [
    { value: 'available', label: 'Disponible', color: 'success' },
    { value: 'ondemand', label: 'Bajo Pedido', color: 'info' },
    { value: 'ask', label: 'Consultar', color: 'warning' },
    { value: 'transit', label: 'En Tránsito', color: 'primary' },
    { value: 'soldout', label: 'Agotado', color: 'danger' }
];

const ProductFormView = () => {
    const { catalogID, productID } = useParams();
    const isEditing = Boolean(productID);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { 
        authUser, vendorProfile, catalogs, products, error, infoMessage 
    } = useSelector((state) => state.admin);

    const catalog = catalogs.find(c => c.id === catalogID);
    const existingProduct = products.find(p => p.id === productID);

    const [productForm, setProductForm] = useState({ 
        id: null, 
        name: '', 
        price: '', 
        discount: '', 
        description: '', 
        stock: 'available', 
        coverRef: null,
        pictures: {}
    });

    const [imageFiles, setImageFiles] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (authUser?.uid && catalogID) {
            if (!catalog) {
                dispatch(fetchSingleCatalog({ userID: authUser.uid, catalogID }));
            }
            dispatch(fetchAdminProducts({ userID: authUser.uid, catalogID }));
        }
    }, [dispatch, authUser, catalogID, catalog]);

    useEffect(() => {
        dispatch(clearAdminError());
        if (isEditing && existingProduct) {
            let pictures = { ...(existingProduct.pictures || {}) };
            if (Array.isArray(existingProduct.pictures)) {
                const picsMap = {};
                existingProduct.pictures.forEach((url, i) => {
                    picsMap[`pic_${i}`] = typeof url === 'string' ? url : url.url;
                });
                pictures = picsMap;
            }
            const cover = existingProduct.coverUrl || existingProduct.imageUrl || existingProduct.cover;
            if (cover && !Object.values(pictures).includes(cover)) {
                pictures[existingProduct.coverRef || 'cover_main'] = cover;
            }
            setProductForm({
                id: existingProduct.id,
                name: existingProduct.name || '',
                price: existingProduct.price || '',
                discount: existingProduct.discount || '',
                description: existingProduct.description || '',
                stock: existingProduct.stock || 'available',
                coverRef: existingProduct.coverRef || null,
                coverUrl: existingProduct.coverUrl || cover || '',
                pictures
            });
        }
    }, [isEditing, existingProduct, dispatch]);

    const isPremiumV2 = vendorProfile?.subscription === 'premiumv2';
    const productLimitReached = !isEditing && !isPremiumV2 && products.length >= 25;
    const currencySymbol = catalog ? getCurrencySymbol(catalog) : '$';

    const existingImagesCount = Object.keys(productForm.pictures || {}).length;

    const handleImageFilesChange = (e) => {
        const files = Array.from(e.target.files);
        const allowedCount = Math.max(0, 4 - existingImagesCount);
        const selectedFiles = files.slice(0, allowedCount);
        setImageFiles(selectedFiles);
        const previews = selectedFiles.map(file => URL.createObjectURL(file));
        setImagePreviews(previews);
    };

    const handleSaveProduct = async (e) => {
        e.preventDefault();
        setSaving(true);
        const res = await dispatch(saveProduct({
            userID: authUser.uid,
            catalogID,
            productData: {
                ...productForm,
                price: parseFloat(productForm.price) || 0,
                discount: parseFloat(productForm.discount) || 0
            },
            imageFiles
        }));
        setSaving(false);
        if (!res.error) {
            navigate(`/admin/dashboard/${catalogID}`);
        }
    };

    const handleSetCover = async (imageID) => {
        if (isEditing && existingProduct) {
            await dispatch(setProductCover({
                userID: authUser.uid,
                catalogID,
                productID: existingProduct.id,
                imageID
            }));
            const newCoverUrl = typeof productForm.pictures[imageID] === 'string' 
                ? productForm.pictures[imageID] 
                : productForm.pictures[imageID]?.url;
            setProductForm(prev => ({ ...prev, coverRef: imageID, coverUrl: newCoverUrl || prev.coverUrl }));
        }
    };

    const handleDeleteProductImage = async (imageID) => {
        if (isEditing && existingProduct && window.confirm("¿Eliminar esta imagen del producto?")) {
            await dispatch(deleteProductImage({
                userID: authUser.uid,
                catalogID,
                productID: existingProduct.id,
                imageID
            }));
            setProductForm(prev => {
                const newPics = { ...prev.pictures };
                delete newPics[imageID];
                let coverRef = prev.coverRef;
                if (coverRef === imageID) {
                    coverRef = Object.keys(newPics)[0] || null;
                }
                return { ...prev, pictures: newPics, coverRef };
            });
        }
    };

    return (
        <div className="bg-gray-50 min-vh-100 font-sans pb-60">
            <AdminNavbar />
            <AdminBreadcrumb 
                catalog={catalog} 
                product={existingProduct} 
                currentAction={isEditing ? 'Editar Producto' : 'Nuevo Producto'} 
            />

            <div className="container container-lg" style={{ maxWidth: '840px' }}>
                <div className="bg-white p-24 p-md-32 rounded-24 border shadow-2xs">
                    {/* Header */}
                    <div className="mb-24 pb-16 border-bottom flex-between">
                        <div>
                            <h4 className="fw-bold text-gray-900 mb-2">
                                {isEditing ? 'Editar Producto' : 'Crear Nuevo Producto'}
                            </h4>
                            <p className="text-gray-500 text-xs mb-0">
                                Catálogo: <strong className="text-gray-900">{catalog?.name}</strong> • Moneda: <strong className="text-gray-900">{currencySymbol}</strong>
                            </p>
                        </div>
                    </div>

                    {error && (
                        <div className="alert alert-danger py-12 px-16 text-xs rounded-12 mb-20 flex-align gap-8" role="alert">
                            <i className="ph ph-warning-circle text-md"></i>
                            <span>{error}</span>
                        </div>
                    )}

                    {infoMessage && (
                        <div className="alert alert-success py-12 px-16 text-xs rounded-12 mb-20 flex-align gap-8" role="alert">
                            <i className="ph ph-check-circle text-md"></i>
                            <span>{infoMessage}</span>
                        </div>
                    )}

                    {productLimitReached ? (
                        <div className="text-center py-40 border border-dashed rounded-16 bg-gray-50">
                            <i className="ph ph-warning-circle text-3xl text-warning mb-8 d-block"></i>
                            <h6 className="fw-bold text-gray-800 mb-4">Límite de productos alcanzado</h6>
                            <p className="text-gray-500 text-xs mb-16">
                                El plan gratuito permite un máximo de 25 productos por catálogo.
                            </p>
                            <button 
                                type="button" 
                                className="btn btn-outline-gray-200 rounded-pill px-20 text-xs fw-semibold"
                                onClick={() => navigate(`/admin/dashboard/${catalogID}`)}
                            >
                                Volver al Catálogo
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSaveProduct}>
                            <div className="row g-16 mb-16">
                                {/* Product Name */}
                                <div className="col-12">
                                    <label className="form-label text-xs fw-bold text-gray-700 text-uppercase">Nombre del Producto *</label>
                                    <input 
                                        type="text" 
                                        className="form-control rounded-12 py-10 px-16 text-sm"
                                        placeholder="Ej: Camisa Manga Larga Oxford"
                                        value={productForm.name}
                                        onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="row g-16 mb-16">
                                {/* Price */}
                                <div className="col-md-4">
                                    <label className="form-label text-xs fw-bold text-gray-700 text-uppercase">Precio ({currencySymbol}) *</label>
                                    <input 
                                        type="number" 
                                        step="any"
                                        className="form-control rounded-12 py-10 px-16 text-sm"
                                        placeholder="0.00"
                                        value={productForm.price}
                                        onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                                        required
                                    />
                                </div>

                                {/* Discount % */}
                                <div className="col-md-4">
                                    <label className="form-label text-xs fw-bold text-gray-700 text-uppercase">% Descuento</label>
                                    <input 
                                        type="number" 
                                        step="any"
                                        min="0"
                                        max="100"
                                        className="form-control rounded-12 py-10 px-16 text-sm"
                                        placeholder="0"
                                        value={productForm.discount}
                                        onChange={(e) => setProductForm({ ...productForm, discount: e.target.value })}
                                    />
                                </div>

                                {/* Stock Status */}
                                <div className="col-md-4">
                                    <label className="form-label text-xs fw-bold text-gray-700 text-uppercase">Estado de Stock</label>
                                    <select 
                                        className="form-select rounded-12 py-10 px-16 text-sm"
                                        value={productForm.stock}
                                        onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })}
                                    >
                                        {STOCK_STATUSES.map(s => (
                                            <option key={s.value} value={s.value}>{s.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Description */}
                            <div className="mb-20">
                                <label className="form-label text-xs fw-bold text-gray-700 text-uppercase">Descripción Detallada</label>
                                <textarea 
                                    className="form-control rounded-12 py-10 px-16 text-sm"
                                    rows="4"
                                    placeholder="Detalles del producto, tallas, materiales, características..."
                                    value={productForm.description}
                                    onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                                ></textarea>
                            </div>

                            {/* Existing Images Gallery Manager */}
                            {isEditing && Object.keys(productForm.pictures || {}).length > 0 && (
                                <div className="mb-20 p-16 bg-gray-50 rounded-16 border">
                                    <label className="form-label text-xs fw-bold text-gray-700 text-uppercase mb-8">Imágenes Registradas</label>
                                    <div className="d-flex flex-wrap gap-12">
                                        {Object.entries(productForm.pictures).map(([imgId, imgObj]) => {
                                            const url = resolveImageUrl(imgObj);
                                            const isCover = productForm.coverRef === imgId;

                                            return (
                                                <div 
                                                    key={imgId} 
                                                    className={`position-relative rounded-12 border overflow-hidden ${
                                                        isCover ? 'border-main-600 ring-2 ring-main-600' : 'border-gray-200'
                                                    }`} 
                                                    style={{ width: '90px', height: '90px' }}
                                                >
                                                    <img src={url} alt={productForm.name || "Producto"} className="w-100 h-100 object-fit-cover" />
                                                    
                                                    {isCover && (
                                                        <span className="position-absolute top-0 start-0 bg-main-600 text-white text-3xs px-6 py-2 fw-bold">
                                                            Portada
                                                        </span>
                                                    )}

                                                    <div className="position-absolute bottom-0 start-0 end-0 bg-black-50 p-4 flex-between">
                                                        {!isCover && (
                                                            <button 
                                                                type="button" 
                                                                className="btn btn-xs btn-light text-3xs p-2 text-main-600 fw-bold"
                                                                onClick={() => handleSetCover(imgId)}
                                                                title="Establecer portada"
                                                            >
                                                                Portada
                                                            </button>
                                                        )}
                                                        <button 
                                                            type="button" 
                                                            className="btn btn-xs btn-danger text-3xs p-2 ms-auto"
                                                            onClick={() => handleDeleteProductImage(imgId)}
                                                            title="Eliminar imagen"
                                                        >
                                                            <i className="ph ph-trash"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Upload New Images (Max 4 Total) */}
                            <div className="mb-24">
                                <div className="d-flex align-items-center justify-content-between mb-8">
                                    <label className="form-label text-xs fw-bold text-gray-700 text-uppercase mb-0">
                                        Subir {isEditing ? 'Nuevas' : ''} Imágenes (Máximo 4)
                                    </label>
                                    <span className="badge bg-main-100 text-main-700 text-3xs rounded-pill px-8 py-4 fw-bold">
                                        {existingImagesCount + imageFiles.length} / 4 imágenes
                                    </span>
                                </div>

                                <input 
                                    type="file" 
                                    accept="image/*"
                                    multiple
                                    disabled={existingImagesCount >= 4}
                                    className="form-control rounded-12 text-sm"
                                    onChange={handleImageFilesChange}
                                />
                                {existingImagesCount >= 4 ? (
                                    <span className="text-2xs text-warning-600 mt-4 d-block fw-semibold">
                                        Se ha alcanzado el límite máximo de 4 imágenes para este producto. Elimina una imagen existente para subir una nueva.
                                    </span>
                                ) : (
                                    <span className="text-2xs text-gray-400 mt-4 d-block">
                                        Puedes adjuntar hasta {4 - existingImagesCount} imagen(es) más. Se optimizarán automáticamente antes de subir.
                                    </span>
                                )}

                                {imagePreviews.length > 0 && (
                                    <div className="d-flex flex-wrap gap-8 mt-12">
                                        {imagePreviews.map((prev, i) => (
                                            <img key={i} src={prev} alt="New preview" className="w-70 h-70 rounded-12 object-fit-cover border shadow-2xs" />
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Form Footer */}
                            <div className="pt-20 border-top flex-between">
                                <button 
                                    type="button" 
                                    className="btn btn-outline-gray-200 rounded-pill px-20 py-10 text-xs fw-semibold" 
                                    onClick={() => navigate(`/admin/dashboard/${catalogID}`)}
                                >
                                    Cancelar
                                </button>

                                <button 
                                    type="submit" 
                                    className="btn btn-main rounded-pill px-24 py-10 text-xs fw-bold flex-align gap-6 shadow-xs" 
                                    disabled={saving}
                                >
                                    {saving ? 'Guardando...' : (isEditing ? 'Actualizar Producto' : 'Guardar Producto')}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProductFormView;
